import melodyStyleData from '@/data/melodyStyles.json';
import { parseChord } from './chords';
import { resolveStarterScale, scalePitches } from './scaleContract';

const mod = (value, base = 12) => ((value % base) + base) % base;
const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const roundBeat = (value) => Math.round(value * 96) / 96;
const overlap = (left, right) => left.start_bar < right.start_bar + Number(right.bars || 0) && right.start_bar < left.start_bar + Number(left.bars || 0);
const STYLE_FALLBACKS = Object.freeze({ hook: ['A', 'A', 'A', 'B'], lead: ['A', 'A2', 'B', 'B2'], counter: ['ARP', 'ARP', 'ARP', 'ARP'] });

function chordAtArrangementBar(clip, arrangementBar) {
  const pattern = clip.pattern || {};
  const patternBars = Number(pattern.patternBars || 1);
  const sourceBar = mod(Math.floor(arrangementBar - clip.start_bar + Number(clip.offset || 0)), patternBars);
  return pattern.chords?.find((chord) => chord.bar === sourceBar) || null;
}

export function resolveChordSource({ clip, tracks = [] } = {}) {
  if (clip?.pattern?.chords?.length) return { clip, pattern: clip.pattern, chords: clip.pattern.chords, family: clip.pattern.chordSource?.family || 'trap', packId: clip.pattern.chordSource?.packId || null };
  const candidate = tracks.flatMap((track, trackIndex) => (track.clips || []).map((item) => ({ item, trackIndex })))
    .filter(({ item }) => item.isPattern && ['keys', 'sampler'].includes(item.patternType) && item.pattern?.chords?.length && overlap(clip, item))
    .sort((a, b) => a.trackIndex - b.trackIndex || Number(a.item.start_bar) - Number(b.item.start_bar))[0]?.item;
  if (!candidate) return null;
  const bars = Math.max(1, Number(clip.pattern?.patternBars || clip.bars || 1));
  const chords = Array.from({ length: bars }, (_, bar) => {
    const found = chordAtArrangementBar(candidate, clip.start_bar + bar);
    return found ? { ...found, bar } : null;
  }).filter(Boolean);
  return { clip: candidate, pattern: candidate.pattern, chords, family: candidate.pattern.chordSource?.family || 'trap', packId: candidate.pattern.chordSource?.packId || null };
}

function seeded(seed) {
  let value = (Number(seed) || 1) >>> 0;
  return () => { value += 0x6D2B79F5; let t = value; t = Math.imul(t ^ t >>> 15, t | 1); t ^= t + Math.imul(t ^ t >>> 7, t | 61); return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}

function familyConfig(family) {
  return melodyStyleData.families[family] || melodyStyleData.families.trap;
}

export function melodyPhrase(style = 'hook') {
  return [...(melodyStyleData.styles?.[style]?.phrase || STYLE_FALLBACKS[style] || STYLE_FALLBACKS.hook)];
}

function chordPitches(symbol, low = 60, high = 79) {
  const chord = parseChord(symbol);
  if (!chord) return [clamp(60, low, high)];
  const classes = new Set(chord.intervals.map((interval) => mod(chord.rootPitch + interval)));
  return Array.from({ length: high - low + 1 }, (_, index) => low + index).filter((pitch) => classes.has(mod(pitch)));
}

const nearest = (values, target) => [...values].sort((a, b) => Math.abs(a - target) - Math.abs(b - target) || b - a)[0];
const chordForBar = (chords, bar) => chords.find((item) => Number(item.bar) === Number(bar)) || chords[bar % Math.max(1, chords.length)] || chords[0] || { symbol: 'C' };
const pitchClassInChord = (pitch, chord) => chordPitches(chord?.symbol, 0, 11).some((candidate) => mod(candidate) === mod(pitch));

function strongStep(relativeStart, grid) {
  const stepLength = 4 / grid;
  const exactStep = Math.round(relativeStart / stepLength) + 1;
  if (Math.abs((exactStep - 1) * stepLength - relativeStart) > 0.001) return false;
  return (grid === 12 ? [1, 4, 7, 10] : [1, 9]).includes(exactStep);
}

export function isStrongMelodyBeat(note, grid = 16) {
  return strongStep(Number(note.start) - Number(note.bar || 0) * 4, grid);
}

function scaleStepFrom(pitch, direction, scale, low, high) {
  const pitches = scalePitches(scale, low, high);
  if (!pitches.length) return clamp(pitch + direction, low, high);
  if (direction > 0) return pitches.find((candidate) => candidate > pitch) ?? pitches.at(-1);
  return [...pitches].reverse().find((candidate) => candidate < pitch) ?? pitches[0];
}

function scaleStepDistance(left, right, scale, low, high) {
  const pitches = scalePitches(scale, low, high);
  const leftIndex = pitches.indexOf(left); const rightIndex = pitches.indexOf(right);
  return leftIndex >= 0 && rightIndex >= 0 ? Math.abs(rightIndex - leftIndex) : Infinity;
}

function scaleStepsFrom(pitch, steps, scale, low, high) {
  let result = pitch;
  for (let index = 0; index < Math.abs(steps); index += 1) result = scaleStepFrom(result, Math.sign(steps), scale, low, high);
  return result;
}

function chordToneClasses(symbol) {
  const chord = parseChord(symbol);
  return chord ? [...new Set(chord.intervals.map((interval) => mod(chord.rootPitch + interval)))] : [0];
}

function chordAnchor(chord, low, high, ordinal, target) {
  const classes = chordToneClasses(chord?.symbol);
  const pitchClass = classes[mod(ordinal, classes.length)];
  const choices = Array.from({ length: high - low + 1 }, (_, index) => low + index).filter((pitch) => mod(pitch) === pitchClass);
  return nearest(choices, target) ?? nearest(chordPitches(chord?.symbol, low, high), target) ?? low;
}

function rhythmSubset(config, feel, seed, random) {
  const grid = Number(config.stepsPerBar || 16);
  const beats = clamp(Number(feel.motifBeats) || 1, 0.5, 4);
  const cells = config.cells?.length ? config.cells : [[{ step: 1, len: grid / 4, role: 'chord' }]];
  const source = cells[mod(seed - 1, cells.length)];
  let rhythm = source.map((item) => ({
    offset: (Number(item.step) - 1) * 4 / grid,
    length: Number(item.len) * 4 / grid,
    role: item.role === 'chord' ? 'chord' : 'pass',
  })).filter((item) => item.offset < beats).map((item) => ({ ...item, length: Math.max(1 / 32, Math.min(item.length, beats - item.offset)) }));
  if (!rhythm.some((item) => Math.abs(item.offset) < .001)) rhythm.push({ offset: 0, length: 4 / grid, role: 'chord' });
  rhythm = rhythm.sort((a, b) => a.offset - b.offset).filter((item, index, items) => !index || Math.abs(item.offset - items[index - 1].offset) > .001);
  if (rhythm.length > 3) {
    const first = rhythm[0];
    const rest = rhythm.slice(1).filter(() => random() >= Number(feel.rest || 0));
    rhythm = [first, ...rest];
  }
  if (mod(seed - 1, 3) === 2) {
    if (rhythm.length > 3) rhythm.splice(1 + mod(seed, rhythm.length - 1), 1);
    else if (rhythm.length === 3) {
      const step = 4 / grid;
      for (let index = rhythm.length - 1; index > 0; index -= 1) {
        const shifted = rhythm[index].offset + step;
        if (shifted < beats && !rhythm.some((item, itemIndex) => itemIndex !== index && Math.abs(item.offset - shifted) < .001)) {
          rhythm[index] = { ...rhythm[index], offset: shifted };
          break;
        }
      }
    }
  }
  const available = Array.from({ length: Math.max(3, Math.round(beats * grid / 4)) }, (_, index) => index * 4 / grid)
    .filter((offset) => offset < beats && !rhythm.some((item) => Math.abs(item.offset - offset) < .001));
  while (rhythm.length < 3 && available.length) {
    const choice = mod(seed + rhythm.length * 2, available.length);
    rhythm.push({ offset: available.splice(choice, 1)[0], length: 4 / grid, role: rhythm.length % 2 ? 'pass' : 'chord' });
  }
  rhythm = rhythm.sort((a, b) => a.offset - b.offset).slice(0, Math.max(3, Math.min(rhythm.length, 5)));
  const silentShare = clamp(.22 + Number(feel.rest || 0) * .4, .22, .42);
  const rhythmVariation = 1 - mod(seed - 1, 3) * .08;
  const noteLength = beats * (1 - silentShare) / rhythm.length * rhythmVariation;
  rhythm = rhythm.map((item, index) => {
    const nextOffset = rhythm[index + 1]?.offset ?? beats;
    const gap = nextOffset - item.offset;
    const restCappedLength = grid === 16 && gap >= .25 ? Math.max(1 / 32, gap - .25) : gap;
    return { ...item, length: roundBeat(Math.max(1 / 32, Math.min(noteLength, restCappedLength))) };
  });
  return rhythm;
}

function buildMotif(config, feel, scale, chord, random, seed = 1) {
  const rhythm = rhythmSubset(config, feel, seed, random);

  const [low, high] = feel.range;
  const lowerTop = Math.floor((low + high) / 2);
  const contour = ['rising', 'falling', 'arch', 'valley'][mod(seed - 1, 4)];
  const offsets = rhythm.map((_, index) => {
    if (!index) return 0;
    if (contour === 'rising') return index;
    if (contour === 'falling') return -index;
    if (contour === 'arch') return index <= Math.ceil((rhythm.length - 1) / 2) ? index : -(index - Math.ceil((rhythm.length - 1) / 2));
    return index <= Math.ceil((rhythm.length - 1) / 2) ? -index : index - Math.ceil((rhythm.length - 1) / 2);
  });
  const chordNotes = chordPitches(chord?.symbol, low, high);
  const viable = chordNotes.filter((pitch) => new Set(offsets.map((offset) => scaleStepsFrom(pitch, offset, scale, low, high))).size >= 3);
  const ranked = [...(viable.length ? viable : chordNotes)].sort((a, b) => Math.abs(a - lowerTop) - Math.abs(b - lowerTop) || a - b);
  const startOrdinal = mod(seed - 1, Math.max(1, chordToneClasses(chord?.symbol).length));
  const preferred = chordAnchor(chord, low, high, startOrdinal, lowerTop);
  const first = nearest(ranked, preferred) ?? nearest(scalePitches(scale, low, lowerTop), lowerTop) ?? low;
  const pitches = offsets.map((offset) => scaleStepsFrom(first, offset, scale, low, high));
  return rhythm.map((item, index) => ({ ...item, pitch: pitches[index], interval: pitches[index] - first, shapeStep: offsets[index], contour, startOrdinal }));
}

function tileMotif(motif, motifBeats, bar, anchor, firstPitch) {
  const events = [];
  for (let tile = 0; tile < 4; tile += motifBeats) {
    motif.forEach((item) => {
      const relative = tile + item.offset;
      if (relative >= 4) return;
      events.push({ bar, start: bar * 4 + relative, length: Math.min(item.length, 4 - relative), pitch: anchor + (item.pitch - firstPitch), role: item.role, motifIndex: motif.indexOf(item), shapeStep: item.shapeStep, tile });
    });
  }
  return events;
}

function markVelocities(events, grid) {
  return events.map((event) => ({ ...event, velocity: event.humanization === 'grace' ? .6 : isStrongMelodyBeat(event, grid) ? .9 : .7 }));
}

/** Applies the feel probabilities to an event list. Exported so the probability contract can be tested directly. */
export function applyMelodyFeel(incoming, { feel, scale, chords, grid = 16, random = Math.random, allowAnticipation = true } = {}) {
  let events = incoming.map((event) => ({ ...event }));
  const features = [];
  if (Number(feel?.sync) > 0 && random() < Number(feel.sync)) {
    const candidate = events.find((event) => {
      const relative = event.start - event.bar * 4;
      return relative >= .25 && !strongStep(relative, grid);
    });
    if (candidate) { candidate.start = roundBeat(candidate.start - .25); candidate.humanization = 'sync'; features.push('sync'); }
  }
  if (Number(feel?.triplet) > 0 && random() < Number(feel.triplet)) {
    const index = events.findIndex((event) => isStrongMelodyBeat(event, grid) && event.length >= .5 - .001);
    if (index >= 0) {
      const source = events[index]; const duration = 1 / 6;
      const triplets = Array.from({ length: 3 }, (_, part) => ({ ...source, start: roundBeat(source.start + part * duration), length: roundBeat(duration), pitch: part === 1 ? scaleStepFrom(source.pitch, 1, scale, feel.range[0], feel.range[1]) : source.pitch, shapeStep: Number(source.shapeStep || 0) + (part === 1 ? 1 : 0), humanization: 'triplet' }));
      events.splice(index, 1, ...triplets); features.push('triplet');
    }
  }
  if (Number(feel?.grace) > 0 && random() < Number(feel.grace)) {
    const target = events.find((event) => isStrongMelodyBeat(event, grid) && event.start - event.bar * 4 >= .125);
    if (target) {
      events.push({ ...target, start: roundBeat(target.start - .125), length: .125, pitch: scaleStepFrom(target.pitch, -1, scale, feel.range[0], feel.range[1]), shapeStep: Number(target.shapeStep || 0) - 1, velocity: .6, role: 'grace', humanization: 'grace' });
      features.push('grace');
    }
  }
  if (allowAnticipation && Number(feel?.anticipate) > 0 && random() < Number(feel.anticipate)) {
    const bars = [...new Set(events.map((event) => event.bar))].filter((bar) => bar > 0).sort((a, b) => a - b);
    const bar = bars[0]; const first = events.filter((event) => event.bar === bar).sort((a, b) => a.start - b.start)[0];
    if (first) { first.start = roundBeat(first.start - .5); first.length = roundBeat(first.length + .5); first.bar = Math.max(0, Math.floor(first.start / 4)); first.humanization = 'anticipate'; features.push('anticipate'); }
  }
  return { events: markVelocities(events, grid), features };
}

function soundingBar(event, patternBars) {
  return clamp(Math.floor((Number(event.start) + .0001) / 4), 0, Math.max(0, patternBars - 1));
}

function fitPitchToBar(event, chord, anchor, scale, feel, grid) {
  const [low, high] = feel.range;
  const desired = scaleStepsFrom(anchor, Number(event.shapeStep || 0), scale, low, high);
  if (event.role === 'pass' || event.role === 'grace') return desired;
  if (isStrongMelodyBeat(event, grid)) return nearest(chordPitches(chord?.symbol, low, high), desired) ?? desired;
  return desired;
}

function legalizePitches(events, { chords, scale, feel, grid, patternBars, counter = false }) {
  const [low, high] = feel.range;
  events.forEach((event) => {
    event.bar = soundingBar(event, patternBars);
    event.pitch = clamp(Math.round(event.pitch), low, high);
    const chord = chordForBar(chords, event.bar);
    const chordNotes = chordPitches(chord?.symbol, low, high);
    const scaleNotes = scalePitches(scale, low, high);
    if (counter || isStrongMelodyBeat(event, grid)) {
      event.pitch = nearest(chordNotes, event.pitch) ?? event.pitch;
      if (event.role !== 'resolution') event.role = 'chord';
      return;
    }
    const allowed = event.role === 'pass' || event.role === 'grace' ? scaleNotes : [...new Set([...scaleNotes, ...chordNotes])];
    event.pitch = nearest(allowed, event.pitch) ?? event.pitch;
  });
  events.filter((event) => event.role === 'grace').forEach((grace) => {
    const target = events.filter((event) => event !== grace && event.start >= grace.start + grace.length - .001).sort((a, b) => a.start - b.start)[0];
    if (target) grace.pitch = scaleStepFrom(target.pitch, -1, scale, low, high);
  });
}

function repairGraceNotes(events, scale, feel) {
  const scaleNotes = scalePitches(scale, feel.range[0], feel.range[1]);
  const repaired = [];
  events.forEach((event, index) => {
    if (event.role !== 'grace') { repaired.push(event); return; }
    const target = events.slice(index + 1).find((candidate) => candidate.start >= event.start + event.length - .001);
    const below = [...scaleNotes].reverse().find((pitch) => pitch < target?.pitch);
    if (!target || !Number.isFinite(below)) return;
    event.pitch = below;
    const repeatedBefore = [...repaired].reverse().findIndex((candidate) => candidate.pitch !== below);
    if (repeatedBefore === -1 ? repaired.length >= 3 : repeatedBefore >= 3) return;
    repaired.push(event);
  });
  return repaired;
}

function breakLongRepeats(events, style, scale, feel, chords, grid) {
  const maximum = style === 'counter' ? 1 : 3;
  let repeats = 1;
  for (let index = 1; index < events.length; index += 1) {
    if (events[index].pitch !== events[index - 1].pitch) { repeats = 1; continue; }
    repeats += 1;
    if (repeats <= maximum) continue;
    const event = events[index];
    const allowed = style === 'counter' || isStrongMelodyBeat(event, grid)
      ? chordPitches(chordForBar(chords, event.bar)?.symbol, feel.range[0], feel.range[1])
      : scalePitches(scale, feel.range[0], feel.range[1]);
    const alternatives = allowed.filter((pitch) => pitch !== events[index - 1].pitch);
    event.pitch = nearest(alternatives, event.pitch + (index % 2 ? -2 : 2)) ?? event.pitch;
    repeats = events[index].pitch === events[index - 1].pitch ? repeats : 1;
  }
}

function enforceLeapRecovery(events, scale, feel, chords, grid) {
  const [low, high] = feel.range;
  for (let index = 1; index < events.length; index += 1) {
    const leap = events[index].pitch - events[index - 1].pitch;
    if (Math.abs(leap) <= 5) continue;
    if (index === events.length - 1) {
      if (events[index].role === 'resolution') events[index - 1].pitch = scaleStepFrom(events[index].pitch, -Math.sign(leap), scale, low, high);
      else events[index].pitch = scaleStepFrom(events[index - 1].pitch, Math.sign(leap), scale, low, high);
      continue;
    }
    const next = events[index + 1];
    if (isStrongMelodyBeat(next, grid)) {
      const direction = -Math.sign(leap);
      const desired = scaleStepFrom(events[index].pitch, direction, scale, low, high);
      const nextChord = chordForBar(chords, next.bar);
      if (pitchClassInChord(desired, nextChord)) { next.pitch = desired; continue; }
      if (!isStrongMelodyBeat(events[index], grid) && events[index].role !== 'resolution') {
        events[index].pitch = scaleStepFrom(events[index - 1].pitch, Math.sign(leap), scale, low, high);
        continue;
      }
      const nearby = chordPitches(chordForBar(chords, events[index].bar)?.symbol, low, high).filter((pitch) => Math.abs(pitch - events[index - 1].pitch) <= 5);
      if (nearby.length) { events[index].pitch = nearest(nearby, events[index - 1].pitch); continue; }
    }
    if (events[index + 1].role === 'resolution') {
      const towardResolution = Math.sign(events[index + 1].pitch - events[index].pitch) || -Math.sign(leap);
      events[index].pitch = scaleStepFrom(events[index + 1].pitch, -towardResolution, scale, low, high);
      continue;
    }
    const recovery = events[index + 1].pitch - events[index].pitch;
    if (Math.sign(recovery) === -Math.sign(leap) && scaleStepDistance(events[index].pitch, events[index + 1].pitch, scale, low, high) === 1) continue;
      events[index + 1].pitch = scaleStepFrom(events[index].pitch, -Math.sign(leap), scale, low, high);
  }
}

function monophonic(events, totalBeats) {
  const sorted = [...events].sort((a, b) => a.start - b.start || Number(a.humanization === 'grace') - Number(b.humanization === 'grace'));
  return sorted.map((event, index) => {
    const next = sorted[index + 1];
    const available = (next ? next.start : totalBeats) - event.start;
    return { ...event, start: roundBeat(event.start), length: roundBeat(Math.min(event.length, available, totalBeats - event.start)) };
  }).filter((event) => event.start >= 0 && event.start < totalBeats && event.length > 0);
}

function hookEvents({ chords, motif, feel, scale, patternBars, grid, random }) {
  const [low, high] = feel.range; const phrase = melodyPhrase('hook'); const firstPitch = motif[0].pitch;
  const firstChord = chordForBar(chords, 0); const startOrdinal = Number(motif[0].startOrdinal || 0);
  const firstAnchor = chordAnchor(firstChord, low, high, startOrdinal, firstPitch);
  const rawTemplate = tileMotif(motif, feel.motifBeats, 0, firstAnchor, firstPitch);
  const humanized = applyMelodyFeel(rawTemplate, { feel, scale, chords: [firstChord], grid, random, allowAnticipation: false });
  const template = humanized.events;
  let events = [];
  for (let bar = 0; bar < patternBars; bar += 1) {
    const label = phrase[bar % phrase.length]; const chord = chordForBar(chords, bar);
    const anchor = chordAnchor(chord, low, high, startOrdinal, firstAnchor);
    let translated = template.map((event) => {
      const fitted = { ...event, bar, start: event.start + bar * 4, phrase: label };
      fitted.pitch = fitPitchToBar(fitted, chord, anchor, scale, feel, grid);
      return fitted;
    });
    if (label === 'B' && translated.length) {
      const answerStart = bar * 4 + Math.max(0, 4 - Number(feel.motifBeats || 1));
      const last = translated.find((event) => event.start >= answerStart) || translated.at(-1);
      translated = translated.filter((event) => event.start <= last.start);
      const root = parseChord(chord?.symbol)?.rootPitch;
      const tonicCandidates = scalePitches(scale, low, high).filter((pitch) => mod(pitch) === mod(scale.root));
      const rootCandidates = chordPitches(chord?.symbol, low, high).filter((pitch) => mod(pitch) === mod(root));
      const resolutionCandidates = pitchClassInChord(nearest(tonicCandidates, last.pitch), chord) ? tonicCandidates : rootCandidates;
      last.pitch = nearest(resolutionCandidates, last.pitch) ?? last.pitch;
      last.length = bar * 4 + 4 - last.start;
      last.role = 'resolution';
    }
    events.push(...translated);
  }
  const anticipated = applyMelodyFeel(events, { feel: { ...feel, sync: 0, triplet: 0, grace: 0 }, scale, chords, grid, random, allowAnticipation: true });
  events = anticipated.events;
  return { events, features: [...new Set([...humanized.features, ...anticipated.features])] };
}

function leadEvents({ chords, motif, feel, scale, patternBars, grid, random }) {
  const [low, high] = feel.range; const phrase = melodyPhrase('lead'); const firstPitch = motif[0].pitch; const startOrdinal = Number(motif[0].startOrdinal || 0);
  let events = [];
  for (let bar = 0; bar < patternBars; bar += 1) {
    const label = phrase[bar % phrase.length]; const chord = chordForBar(chords, bar);
    let target = label === 'B' ? Math.floor(low + (high - low) * .78) : label === 'A2' ? Math.floor(low + (high - low) * .58) : Math.floor(low + (high - low) * .45);
    const ordinal = startOrdinal + (label === 'A2' ? 1 : 0);
    const anchor = chordAnchor(chord, low, high, ordinal, target);
    let barEvents = tileMotif(motif, feel.motifBeats, bar, anchor, firstPitch).map((event) => {
      const fitted = { ...event, phrase: label };
      fitted.pitch = fitPitchToBar(fitted, chord, anchor, scale, feel, grid);
      return fitted;
    });
    if (label === 'A') barEvents.forEach((event) => {
      if (event.start - bar * 4 >= 2 && !isStrongMelodyBeat(event, grid)) {
        event.shapeStep = Number(event.shapeStep || 0) - 1;
        event.pitch = fitPitchToBar(event, chord, anchor, scale, feel, grid);
      }
    });
    if (label === 'B2') {
      barEvents = barEvents.filter((event) => event.start - bar * 4 <= 2.001);
      const descending = scalePitches(scale, low, high).filter((pitch) => pitch <= Math.floor(low + (high - low) * .7)).reverse();
      barEvents.forEach((event, index) => { event.pitch = descending[Math.min(index, descending.length - 1)] ?? event.pitch; });
      let resolution = barEvents.find((event) => Math.abs(event.start - (bar * 4 + 2)) < .001);
      if (!resolution) { resolution = { bar, start: bar * 4 + 2, length: 2, pitch: low, velocity: .9, role: 'resolution', phrase: label }; barEvents.push(resolution); }
      const tonic = scalePitches(scale, low, high).filter((pitch) => mod(pitch) === mod(scale.root));
      const chordRoot = parseChord(chord?.symbol)?.rootPitch;
      const roots = chordPitches(chord?.symbol, low, high).filter((pitch) => mod(pitch) === mod(chordRoot));
      resolution.pitch = pitchClassInChord(nearest(tonic, resolution.pitch), chord) ? nearest(tonic, resolution.pitch) : nearest(roots, resolution.pitch);
      resolution.length = 2;
      resolution.role = 'resolution';
    }
    events.push(...barEvents);
  }
  const humanized = applyMelodyFeel(events, { feel, scale, chords, grid, random });
  events = humanized.events;

  for (let block = 0; block < patternBars; block += phrase.length) {
    const peakBar = block + phrase.indexOf('B');
    if (peakBar < block || peakBar >= patternBars) continue;
    const peakCandidates = events.filter((event) => event.bar === peakBar && isStrongMelodyBeat(event, grid));
    const peak = peakCandidates.find((event) => Math.abs(event.start - peakBar * 4 - 2) < .001) || peakCandidates.at(-1);
    if (peak) {
      const highChord = chordPitches(chordForBar(chords, peakBar)?.symbol, low, high).at(-1);
      peak.pitch = highChord ?? nearest(scalePitches(scale, low, high), high) ?? peak.pitch;
      events.forEach((event) => { if (event.bar !== peakBar && event.pitch >= peak.pitch) event.pitch = scaleStepFrom(peak.pitch, -1, scale, low, high); });
    }
  }
  return { events, features: humanized.features };
}

function counterEvents({ chords, family, feel, patternBars, seed }) {
  const [low, high] = feel.range; const lowerHigh = Math.floor((low + high) / 2);
  const step = ['trap', 'drill', 'dance'].includes(family) ? .25 : .5;
  const patterns = ['up', 'down', 'up-down', 'broken']; const pattern = patterns[mod(seed - 1, patterns.length)];
  const events = [];
  let previous = null;
  for (let bar = 0; bar < patternBars; bar += 1) {
    const pitches = chordPitches(chordForBar(chords, bar)?.symbol, low, lowerHigh).sort((a, b) => a - b);
    const indices = Array.from({ length: pitches.length }, (_, index) => index);
    const baseOrder = pattern === 'down' ? [...indices].reverse()
      : pattern === 'up-down' && indices.length > 1 ? [...indices, ...indices.slice(1, -1).reverse()]
        : pattern === 'broken' ? [0, 2, 1, 3, ...indices.slice(4)].filter((index) => index < indices.length)
          : indices;
    const inversion = mod(seed - 1 + bar, Math.max(1, pitches.length));
    const ordered = baseOrder.map((index) => pitches[mod(index + inversion, pitches.length)]);
    for (let start = 0, index = 0; start < 4 - .001; start += step, index += 1) {
      let pitch = ordered[index % Math.max(1, ordered.length)] ?? low;
      if (previous != null && (pitch === previous || Math.abs(pitch - previous) > 5)) {
        const nearby = pitches.filter((candidate) => candidate !== previous && Math.abs(candidate - previous) <= 5);
        pitch = nearest(nearby, pitch) ?? pitch;
      }
      if (pitch === previous) pitch = nearest(pitches.filter((candidate) => candidate !== previous), pitch + (index % 2 ? -3 : 3)) ?? pitch;
      previous = pitch;
      const articulation = mod(seed - 1, 3) === 1 ? .75 : 1;
      events.push({ bar, start: bar * 4 + start, length: step * articulation, pitch, velocity: .8, role: 'chord', phrase: 'ARP' });
    }
  }
  return { events, features: [], counterPattern: pattern, counterStep: step, counterInversion: mod(seed - 1, 4) };
}

function encodeRhythm(notes) {
  return notes.flatMap((note) => [roundBeat(note.start), roundBeat(note.length)]);
}

export function melodyRhythmPlan(family, patternBars, seed) {
  const config = familyConfig(family); const random = seeded(seed);
  return Array.from({ length: patternBars }, () => Math.floor(random() * Math.max(1, config.cells?.length || 1)));
}

export function generateMelody({ chords = [], family = 'trap', packId = null, keySignature = 'C min', patternBars = 4, seed = 1, style = 'hook', feelOverride = null } = {}) {
  const config = familyConfig(family); const selectedStyle = melodyStyleData.styles?.[style] ? style : 'hook';
  const feel = { ...config.feel, ...(feelOverride || {}), range: [...(feelOverride?.range || config.feel.range)] };
  const grid = Number(config.stepsPerBar || 16); const random = seeded(seed); const scale = resolveStarterScale({ packId, family, keySignature });
  const motif = buildMotif(config, feel, scale, chordForBar(chords, 0), random, seed);
  const options = { chords, family, motif, feel, scale, patternBars: Math.max(1, Number(patternBars) || 4), grid, random, seed };
  const built = selectedStyle === 'counter' ? counterEvents(options) : selectedStyle === 'lead' ? leadEvents(options) : hookEvents(options);
  let events = [...built.events].sort((a, b) => a.start - b.start || Number(a.humanization === 'grace') - Number(b.humanization === 'grace'));
  legalizePitches(events, { chords, scale, feel, grid, patternBars: options.patternBars, counter: selectedStyle === 'counter' });
  breakLongRepeats(events, selectedStyle, scale, feel, chords, grid);
  if (selectedStyle !== 'counter') enforceLeapRecovery(events, scale, feel, chords, grid);
  legalizePitches(events, { chords, scale, feel, grid, patternBars: options.patternBars, counter: selectedStyle === 'counter' });
  breakLongRepeats(events, selectedStyle, scale, feel, chords, grid);
  if (selectedStyle !== 'counter') {
    events = repairGraceNotes(events, scale, feel);
    breakLongRepeats(events, selectedStyle, scale, feel, chords, grid);
  }
  if (selectedStyle !== 'counter') events = monophonic(events, options.patternBars * 4);
  else events = events.filter((event) => event.start >= 0 && event.start < options.patternBars * 4);
  events = markVelocities(events, grid);
  const notes = events.map((event, index) => ({ ...event, id: `melody-${selectedStyle}-${seed}-${index}`, pitch: Math.round(event.pitch) }));
  return {
    notes,
    roles: notes.map((note) => note.role),
    melodySeed: seed,
    melodyRhythm: encodeRhythm(notes),
    melodyStyle: selectedStyle,
    style: selectedStyle,
    phrase: melodyPhrase(selectedStyle),
    scale: scale.name,
    motif: { beats: Number(feel.motifBeats), rhythm: motif.map(({ offset, length, role }) => ({ offset, length, role })), intervals: motif.map((item) => item.interval), contour: motif[0]?.contour, startPitch: motif[0]?.pitch },
    feelEvents: [...new Set(built.features || [])],
    counterPattern: built.counterPattern || null,
    counterInversion: built.counterInversion ?? null,
    counterStep: built.counterStep || null,
    range: [...feel.range],
    grid,
  };
}

export function keepRhythmNewNotes(options, existing, seed) {
  const generated = generateMelody({ ...options, style: existing.style || existing.melodyStyle || options.style, seed });
  const [low, high] = generated.range;
  const notes = existing.notes.map((note, index) => ({ ...note, id: `melody-${generated.style}-${seed}-${index}`, pitch: clamp(generated.notes[index % Math.max(1, generated.notes.length)]?.pitch ?? note.pitch, low, high) }));
  if (notes.length && notes.every((note, index) => note.pitch === existing.notes[index].pitch)) {
    const scale = resolveStarterScale({ packId: options.packId, family: options.family, keySignature: options.keySignature });
    const changeIndex = notes.findIndex((note) => !isStrongMelodyBeat(note, generated.grid));
    const index = changeIndex >= 0 ? changeIndex : 0;
    notes[index].pitch = scaleStepFrom(notes[index].pitch, seed % 2 ? -1 : 1, scale, low, high);
  }
  return { ...generated, notes, roles: notes.map((note) => note.role || 'pass'), melodyRhythm: encodeRhythm(notes) };
}

export function keepNotesNewRhythm(options, existing, seed) {
  const generated = generateMelody({ ...options, style: existing.style || existing.melodyStyle || options.style, seed });
  const pitches = existing.notes.map((note) => note.pitch);
  const notes = generated.notes.map((note, index) => ({ ...note, pitch: pitches[index % Math.max(1, pitches.length)] ?? note.pitch }));
  return { ...generated, notes, roles: notes.map((note) => note.role || 'pass') };
}

export const __melodyDataVersion = melodyStyleData.version;
